import React, { useEffect, useState, useMemo } from 'react';
import {
    Box,
    Button,
    Typography,
    CircularProgress,
    TableContainer,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    IconButton,
    Tooltip,
    Chip,
    Menu,
    MenuItem,
    TextField,
    InputAdornment,
    Tabs,
    Tab,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Alert,
    AlertTitle
} from '@mui/material';
import {
    Add as AddIcon,
    MoreVert as MoreVertIcon,
    Refresh as RefreshIcon,
    Search as SearchIcon,
    Delete as DeleteIcon,
    Visibility as VisibilityIcon,
    Edit as EditIcon,
    ExpandMore as ExpandMoreIcon,
    Info as InfoIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import hostService from '../services/hostService';
import AddHostDialog from './AddHostDialog';
import UpdateMaxMemberDialog from './UpdateMaxMemberDialog';
import { toast } from 'react-toastify';
import HomeLayout from '../../../components/layout/HomeLayout';
import LockedModal from '../../subscription/components/LockedModal';
import subscriptionService from '../../../services/subscriptionService';

const HostRow = ({ host, index, onRefresh, onDelete, onManage, onEditMaxMember }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Chưa có';
        return new Date(dateString).toLocaleString('vi-VN');
    };

    return (
        <TableRow hover>
            <TableCell>{index + 1}</TableCell>
            <TableCell sx={{ 
                wordBreak: 'break-all', 
                maxWidth: 200,
                fontSize: { xs: '0.75rem', sm: '0.875rem' }
            }}>
                {host.openaiEmail}
            </TableCell>
            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, fontSize: '0.8rem' }}>{formatDate(host.tokenExpiresAt)}</TableCell>
            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, fontSize: '0.8rem' }}>{formatDate(host.gptExpiresAt)}</TableCell>
            <TableCell>
                <Chip
                    label={host.status}
                    color={host.status === 'ACTIVE' ? 'success' : 'error'}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' } }}
                />
            </TableCell>
            <TableCell align="center" sx={{ whiteSpace: 'nowrap', fontSize: '0.875rem' }}>{host.membersCount} / {host.maxMember || 5}</TableCell>
            <TableCell align="right">
                <Tooltip title="Hành động">
                    <IconButton onClick={handleClick} size="small">
                        <MoreVertIcon />
                    </IconButton>
                </Tooltip>
                <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                >
                    <MenuItem onClick={() => { handleClose(); onManage(host.id); }}>
                        <VisibilityIcon fontSize="small" sx={{ mr: 1 }} /> Xem chi tiết
                    </MenuItem>
                    <MenuItem onClick={() => { handleClose(); onEditMaxMember(host); }}>
                        <EditIcon fontSize="small" sx={{ mr: 1 }} /> Sửa giới hạn
                    </MenuItem>
                    <MenuItem onClick={() => { handleClose(); onRefresh(host.id); }}>
                        <RefreshIcon fontSize="small" sx={{ mr: 1 }} /> Làm mới Token
                    </MenuItem>
                    <MenuItem onClick={() => { handleClose(); onDelete(host.id); }} sx={{ color: 'error.main' }}>
                        <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Xóa
                    </MenuItem>
                </Menu>
            </TableCell>
        </TableRow>
    );
};

const HostList = () => {
    const [hosts, setHosts] = useState([]);
    const [filteredHosts, setFilteredHosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [addDialogOpen, setAddDialogOpen] = useState(false);

    // Subscription Check
    const [isLocked, setIsLocked] = useState(false);
    const [checkingSub, setCheckingSub] = useState(true);
    const [lockReason, setLockReason] = useState(null);

    // New state for update dialog
    const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
    const [selectedHost, setSelectedHost] = useState(null);

    // Filter state
    const [currentTab, setCurrentTab] = useState('ALL');

    const navigate = useNavigate();

    useEffect(() => {
        checkAccess();
    }, []);

    const checkAccess = async () => {
        try {
            setCheckingSub(true);
            const subRes = await subscriptionService.getCurrentSubscription();
            if (!subRes.success || !subRes.data || subRes.data.isExpired) {
                setIsLocked(true);
                if (subRes.data?.isExpired) {
                    setLockReason(`Gói cước của bạn đã hết hạn vào ngày ${new Date(subRes.data.endDate).toLocaleDateString('vi-VN')}. Vui lòng gia hạn để tiếp tục sử dụng.`);
                } else if (subRes.data?.status === 'CANCELLED') {
                    setLockReason('Gói cước của bạn đã bị hủy. Vui lòng đăng ký gói mới.');
                } else {
                    setLockReason('Bạn cần đăng ký gói cước để sử dụng tính năng này.');
                }
            } else {
                fetchHosts();
            }
        } catch (e) {
            console.error(e);
            setIsLocked(true);
            setLockReason('Không thể kiểm tra trạng thái gói cước. Vui lòng thử lại sau.');
        } finally {
            setCheckingSub(false);
        }
    };

    const fetchHosts = async () => {
        setLoading(true);
        try {
            // Fetch ALL hosts to calculate counts client-side
            const data = await hostService.getHosts({ status: 'ALL' });
            setHosts(data);
        } catch (error) {
            if (error.response?.status === 403 && error.response?.data?.code === 'SUBSCRIPTION_REQUIRED') {
                setIsLocked(true);
                setLockReason(error.response.data.message);
            } else if (!isLocked) {
                toast.error('Không thể tải danh sách tài khoản');
            }
        } finally {
            setLoading(false);
        }
    };

    // Memoize counts
    const statusCounts = useMemo(() => {
        const counts = {
            ALL: hosts.length,
            ACTIVE: 0,
            EXPIRED: 0,
            LOGIN_FAILED: 0,
            DISABLED: 0,
            SYNCING: 0
        };
        hosts.forEach(host => {
            if (counts[host.status] !== undefined) {
                counts[host.status]++;
            }
        });
        return counts;
    }, [hosts]);

    // Apply filters
    useEffect(() => {
        let result = hosts;

        // 1. Filter by Status
        if (currentTab !== 'ALL') {
            result = result.filter(host => host.status === currentTab);
        }

        // 2. Filter by Search Term
        if (searchTerm) {
            const lowerTerm = searchTerm.toLowerCase();
            result = result.filter(host =>
                (host.openaiEmail || '').toLowerCase().includes(lowerTerm)
            );
        }

        setFilteredHosts(result);
    }, [hosts, currentTab, searchTerm]);

    const handleTabChange = (event, newValue) => {
        setCurrentTab(newValue);
    };

    const handleDelete = async (hostId) => {
        if (window.confirm('Bạn có chắc chắn muốn xóa tài khoản Host này không?')) {
            try {
                await hostService.deleteHost(hostId);
                toast.success('Đã xóa tài khoản Host');
                fetchHosts();
            } catch (error) {
                toast.error('Xóa thất bại');
            }
        }
    };

    const handleRefresh = async (hostId) => {
        try {
            await hostService.refreshHostToken(hostId);
            toast.success('Đã làm mới token thành công');
            fetchHosts();
        } catch (error) {
            toast.error('Làm mới token thất bại');
        }
    };

    const handleEditMaxMember = (host) => {
        setSelectedHost(host);
        setUpdateDialogOpen(true);
    };

    return (
        <HomeLayout>
            <Box sx={{ p: 3 }}>
                {checkingSub ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
                        <CircularProgress />
                    </Box>
                ) : isLocked ? (
                    <LockedModal alertMessage={lockReason} />
                ) : (
                    <>
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: { xs: 'column', md: 'row' }, // Xuống dòng cho đến màn hình máy tính bảng
                            justifyContent: 'space-between', 
                            mb: 3, 
                            alignItems: { xs: 'stretch', md: 'center' },
                            gap: 2
                        }}>
                            <Typography 
                                variant="h5" 
                                fontWeight="bold"
                                sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}
                            >
                                Quản lý Host GPT
                            </Typography>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => setAddDialogOpen(true)}
                                sx={{ height: 'fit-content', py: 0.5, px: 1.5, fontSize: '0.8rem' }}
                            >
                                Thêm tài khoản
                            </Button>
                        </Box>

                        <Accordion
                            elevation={0}
                            sx={{
                                mb: 3,
                                bgcolor: 'background.paper',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: '12px !important',
                                overflow: 'hidden',
                                '&:before': { display: 'none' } // Remove MUI top line
                            }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <InfoIcon color="info" />
                                    <Typography fontWeight="medium" color="text.primary">Hướng dẫn thao tác Quản lý Host GPT</Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    <AlertTitle>Định dạng Thêm Tài Khoản</AlertTitle>
                                    Khi chọn <b>Thêm tài khoản</b>, vui lòng điền thông tin theo định dạng chuẩn: <br />
                                    <code>email|password|2fa_secret</code> <br />
                                    <i>Ví dụ: example@gmail.com|password|ABCSENQMTMRABCXG3WADAIUPIN...</i>
                                </Alert>

                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }} color="text.primary">2. Đồng bộ & Làm mới (Refresh Token)</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Bản chất hệ thống dùng Access Token để điều khiển Host. Khi Access Token hết hạn hoặc Lỗi đăng nhập:<br />
                                    - Nhấn nút <b>Hành động</b> → <b>Làm mới Token</b>. Hệ thống sẽ tự động dùng thông tin 2FA để sinh mã OTP và Login lấy Token mới.<br />
                                    - Bạn nên kiểm tra định kỳ cột <b>Token Expires</b> để đảm bảo tài khoản luôn sẵn sàng.
                                </Typography>
                            </AccordionDetails>
                        </Accordion>

                        <Paper sx={{ mb: 3 }}>
                            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    placeholder="Tìm kiếm theo email..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                    size="small"
                                />
                            </Box>
                            <Tabs
                                value={currentTab}
                                onChange={handleTabChange}
                                variant="scrollable"
                                scrollButtons="auto"
                                sx={{ px: 2, pt: 1 }}
                            >
                                <Tab label={`Tất cả (${statusCounts.ALL})`} value="ALL" />
                                <Tab label={`Hoạt động (${statusCounts.ACTIVE})`} value="ACTIVE" />
                                <Tab label={`Hết hạn (${statusCounts.EXPIRED})`} value="EXPIRED" />
                                <Tab label={`Lỗi đăng nhập (${statusCounts.LOGIN_FAILED})`} value="LOGIN_FAILED" />
                                <Tab label={`Vô hiệu hóa (${statusCounts.DISABLED})`} value="DISABLED" />
                            </Tabs>
                        </Paper>

                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <TableContainer component={Paper}>
                                    <Table sx={{ minWidth: 700 }}>
                                        <TableHead sx={{ bgcolor: 'action.hover' }}>
                                            <TableRow>
                                                <TableCell width="5%" sx={{ fontWeight: 'bold' }}>STT</TableCell>
                                                <TableCell width="25%" sx={{ fontWeight: 'bold' }}>Email</TableCell>
                                                <TableCell width="20%" sx={{ fontWeight: 'bold', display: { xs: 'none', md: 'table-cell' } }}>Token Expires</TableCell>
                                                <TableCell width="15%" sx={{ fontWeight: 'bold', display: { xs: 'none', sm: 'table-cell' } }}>Ngày hết hạn</TableCell>
                                                <TableCell width="10%" sx={{ fontWeight: 'bold' }}>Trạng thái</TableCell>
                                                <TableCell width="10%" align="center" sx={{ fontWeight: 'bold' }}>Thành viên</TableCell>
                                                <TableCell width="15%" align="right" sx={{ fontWeight: 'bold' }}>Hành động</TableCell>
                                            </TableRow>
                                        </TableHead>
                                    <TableBody>
                                        {filteredHosts.length > 0 ? (
                                            filteredHosts.map((host, index) => (
                                                <HostRow
                                                    key={host.id}
                                                    host={host}
                                                    index={index}
                                                    onDelete={handleDelete}
                                                    onRefresh={handleRefresh}
                                                    onManage={(id) => navigate(`/hosts/${id}`)}
                                                    onEditMaxMember={handleEditMaxMember}
                                                />
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                                                    <Typography color="text.secondary">
                                                        Không tìm thấy tài khoản nào.
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}

                        <AddHostDialog
                            open={addDialogOpen}
                            onClose={() => setAddDialogOpen(false)}
                            onHostAdded={fetchHosts}
                        />

                        {selectedHost && (
                            <UpdateMaxMemberDialog
                                open={updateDialogOpen}
                                onClose={() => { setUpdateDialogOpen(false); setSelectedHost(null); }}
                                host={selectedHost}
                                onUpdateSuccess={fetchHosts}
                            />
                        )}
                    </>
                )}
            </Box>
        </HomeLayout>
    );
};

export default HostList;
